import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

interface QuickAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: "default" | "danger" | "success";
  disabled?: boolean;
}

interface QuickActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  actions: QuickAction[];
}

const variantStyles = {
  default: {
    button: "hover:bg-primary/5 hover:border-primary/30",
    icon: "text-primary bg-primary/10",
  },
  danger: {
    button: "hover:bg-danger/5 hover:border-danger/30",
    icon: "text-danger bg-danger/10",
  },
  success: {
    button: "hover:bg-success/5 hover:border-success/30",
    icon: "text-success bg-success/10",
  },
};

const QuickActions = React.forwardRef<HTMLDivElement, QuickActionsProps>(
  ({ title, actions, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("rounded-xl border bg-card text-card-foreground shadow", className)}
        {...props}
      >
        <div className="p-4 border-b">
          <h3 className="font-semibold">{title}</h3>
        </div>
        <div className="p-4 space-y-2">
          {actions.map((action, index) => {
            const variant = action.variant || "default";
            const styles = variantStyles[variant];

            return (
              <Button
                key={index}
                variant="outline"
                className={cn(
                  "w-full justify-start gap-3 h-auto py-3",
                  styles.button,
                  action.disabled && "opacity-50 cursor-not-allowed"
                )}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                <div
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-lg",
                    styles.icon
                  )}
                >
                  <div className="h-5 w-5">{action.icon}</div>
                </div>
                <span className="font-medium">{action.label}</span>
              </Button>
            );
          })}
        </div>
      </div>
    );
  }
);
QuickActions.displayName = "QuickActions";

export { QuickActions };
export type { QuickActionsProps, QuickAction };
